/**
 * REST client for the artium-teacher-hiring service (Academics → Teacher
 * Hiring). Cross-origin: the service allowlists CMS origins via CORS. Auth is
 * the same CMS-issued JWT the rest of adminv2 sends, verified by the
 * service's CmsAdminGuard (any ROLE_ADMIN*).
 *
 * Contract: artium-teacher-hiring docs/API.md.
 *
 * REACT_APP_TEACHER_HIRING_BASEURL unset outside local dev means the service
 * isn't deployed for this environment yet; pages say so instead of failing.
 */

const configured = (process.env.REACT_APP_TEACHER_HIRING_BASEURL || '').replace(/\/$/, '');

export const HIRING_BASEURL =
  configured || (process.env.NODE_ENV === 'development' ? 'http://localhost:3600' : '');

export const HIRING_ENABLED = Boolean(HIRING_BASEURL);

export async function hiringApi<T = unknown>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const rawToken = localStorage.getItem('authToken') || '';
  const authHeader = rawToken ? (rawToken.startsWith('Bearer ') ? rawToken : `Bearer ${rawToken}`) : '';

  const res = await fetch(`${HIRING_BASEURL}${path}`, {
    method: init.method ?? 'GET',
    headers: {
      ...(authHeader ? { Authorization: authHeader } : {}),
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = Array.isArray((body as any).message)
      ? (body as any).message.join(', ')
      : (body as any).message || res.statusText;
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return body as T;
}

/** A message for a failed call, including "couldn't reach the service at all". */
export const errorText = (error: unknown): string =>
  error instanceof TypeError
    ? 'Could not reach the Teacher Recruitment service. Check your network, or ask the tech team whether it is running for this environment.'
    : (error as Error)?.message || 'Something went wrong';

// ---------------------------------------------------------------------------
// Shapes (docs/API.md)
// ---------------------------------------------------------------------------

export const FUNNEL = ['NEW', 'SCREENING', 'INTERVIEW_1', 'INTERVIEW_2', 'TRAINING', 'CONTRACT', 'ONBOARDED'] as const;
export const PARKED = ['ON_HOLD', 'REJECTED', 'WITHDRAWN'] as const;
export type Stage = (typeof FUNNEL)[number] | (typeof PARKED)[number];
export type StageStatus = 'PENDING' | 'SCHEDULED' | 'IN_PROGRESS' | 'AWAITING_REVIEW';
export type LeadSource = 'WEBSITE' | 'META' | 'OUTBOUND' | 'JOB_PORTAL' | 'REFERRAL' | 'OTHER';
export type Recommendation = 'ADVANCE' | 'REVIEW' | 'REJECT';

export const STAGE_LABELS: Record<Stage, string> = {
  NEW: 'New lead',
  SCREENING: 'Screening call',
  INTERVIEW_1: 'Interview 1 (Video)',
  INTERVIEW_2: 'Interview 2 (Academic)',
  TRAINING: 'Training',
  CONTRACT: 'Contract',
  ONBOARDED: 'Onboarded',
  ON_HOLD: 'On hold',
  REJECTED: 'Rejected',
  WITHDRAWN: 'Withdrawn',
};

export const STAGE_STATUS_LABELS: Record<StageStatus, string> = {
  PENDING: 'Pending',
  SCHEDULED: 'Scheduled',
  IN_PROGRESS: 'In progress',
  AWAITING_REVIEW: 'Awaiting review',
};

export const SOURCE_LABELS: Record<LeadSource, string> = {
  WEBSITE: 'Website',
  META: 'Meta ads',
  OUTBOUND: 'Outbound call',
  JOB_PORTAL: 'Job portal',
  REFERRAL: 'Teacher referral',
  OTHER: 'Other',
};

/** Mirrors src/pipeline/stages.ts canMove, so the board only offers legal moves. The server still decides. */
export function canMove(from: Stage, to: Stage): boolean {
  if (from === to) return false;
  if (from === 'REJECTED' || from === 'WITHDRAWN') return to === 'NEW';
  if (from === 'ON_HOLD') return (FUNNEL as readonly string[]).includes(to);
  if (from === 'ONBOARDED') return to === 'WITHDRAWN';
  if ((PARKED as readonly string[]).includes(to)) return true;
  const delta = FUNNEL.indexOf(to as (typeof FUNNEL)[number]) - FUNNEL.indexOf(from as (typeof FUNNEL)[number]);
  return delta > 0 || delta === -1;
}

export const needsReason = (to: Stage) => to === 'REJECTED' || to === 'ON_HOLD' || to === 'WITHDRAWN';

export const nextStage = (stage: Stage): Stage | null => {
  const i = FUNNEL.indexOf(stage as (typeof FUNNEL)[number]);
  return i >= 0 && i < FUNNEL.length - 1 ? FUNNEL[i + 1] : null;
};

export interface Card {
  id: string;
  name: string;
  phone: string;
  email: string | null;
  city: string | null;
  genres: string[];
  experienceYears: number | null;
  source: LeadSource;
  sourceDetail: string | null;
  stage: Stage;
  stageStatus: StageStatus;
  stageChangedAt: string;
  ownerEmail: string | null;
  referrerTeacherName: string | null;
  rejectionReason: string | null;
  createdAt: string;
  lastActivityAt: string;
  screening: { score: number | null; recommendation: Recommendation | null } | null;
  interview: { round: 'AI_AVATAR' | 'ACADEMIC'; score: number | null; recommendation: Recommendation | null; result: string | null } | null;
}

export interface BoardColumn {
  stage: Stage;
  count: number;
  awaitingReview: number;
  candidates: Card[];
}

export interface ScreeningAssessment {
  summary: string;
  answers: Array<{ questionId: string; answer: string; score: number; comment: string }>;
  overallScore: number;
  knockout: string | null;
  redFlags: string[];
  recommendation: Recommendation;
}

export interface InterviewAssessment {
  summary: string;
  dimensions: Array<{ key: string; score: number; evidence: string }>;
  strengths: string[];
  concerns: string[];
  overallScore: number;
  recommendation: Recommendation;
}

export interface Question {
  id: string;
  text: string;
  guidance: string;
  weight: number;
  knockout?: string;
}

export interface ScreeningCall {
  id: string;
  attempt: number;
  status: string;
  scheduledFor: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  endedReason: string | null;
  recordingUrl: string | null;
  transcript: string | null;
  questionnaireSnapshot: Question[] | null;
  assessment: ScreeningAssessment | null;
  score: number | null;
  recommendation: Recommendation | null;
  error: string | null;
  createdAt: string;
}

export interface AcademicScorecard {
  scores: Record<string, number>;
  levelFit?: string;
  notes: string;
}

export interface Interview {
  id: string;
  round: 'AI_AVATAR' | 'ACADEMIC';
  status: string;
  scheduledAt: string | null;
  sessionUrl: string | null;
  consentAt: string | null;
  consentName: string | null;
  consentVersion: string | null;
  transcript: string | null;
  recordingUrl: string | null;
  /** Where Agora writes the recording; the server finds the MP4 there when recordingUrl is still empty. */
  recordingPrefix: string | null;
  /** When each question appeared in the recording, for jumping to answers. */
  questionMarkers: Array<{ index: number; question: string; atSeconds: number }> | null;
  provider: string | null;
  /** S3 key of the performance video (uploaded, or recorded on the portal once found). */
  performanceVideoKey: string | null;
  /** A link the candidate pasted (YouTube, Drive) instead. */
  performanceVideoUrl: string | null;
  /** Set when the performance video was recorded on the portal. */
  performanceRecordedAt: string | null;
  /** The language the candidate took the video interview in. */
  language: InterviewLanguage;
  interviewerEmail: string | null;
  meetingUrl: string | null;
  scorecard: AcademicScorecard | null;
  assessment: InterviewAssessment | null;
  score: number | null;
  recommendation: Recommendation | null;
  result: 'PASS' | 'HOLD' | 'FAIL' | null;
  feedback: string | null;
  decidedBy: string | null;
  createdAt: string;
}

export interface CandidateDetail extends Omit<Card, 'screening' | 'interview'> {
  languages: string[];
  qualification: string | null;
  resumeUrl: string | null;
  portfolioUrl: string | null;
  referrerTeacherId: number | null;
  trainingBatch: string | null;
  trainingStartAt: string | null;
  contractUrl: string | null;
  contractSentAt: string | null;
  contractSignedAt: string | null;
  cmsTeacherId: string | null;
  portalUrl: string | null;
  intakeRaw: Record<string, unknown> | null;
  screeningCalls: ScreeningCall[];
  interviews: Interview[];
  messages: Array<{ id: string; channel: 'WHATSAPP' | 'EMAIL'; kind: string; status: string; to: string; error: string | null; createdAt: string }>;
  events: Array<{ id: string; type: string; fromStage: Stage | null; toStage: Stage | null; actor: string; note: string | null; createdAt: string }>;
}

/** Video interview languages (English is always offered). */
export type InterviewLanguage = 'en' | 'hi' | 'te' | 'ta';
export const INTERVIEW_LANGUAGE_LABELS: Record<InterviewLanguage, string> = { en: 'English', hi: 'Hindi', te: 'Telugu', ta: 'Tamil' };

export interface Genre {
  id: string;
  name: string;
  active: boolean;
  brochureUrl: string | null;
  sortOrder: number;
  /** Besides English, the video interview languages offered to its candidates. */
  interviewLanguages: InterviewLanguage[];
  questions: Question[];
  questionnaireVersion: number | null;
}

export interface HiringSettings {
  interviewVideos: Array<{ key: string; title: string; description: string; url: string }>;
  consent: { version: string; text: string };
  callingHours: { startHour: number; endHour: number };
  interviewQuestions: string[];
  whatsappTemplates: Record<string, string>;
  academicBookingUrl: string;
  portalHeroImageUrl: string;
}

export interface HiringMeta {
  academicCriteria: Array<{ key: string; label: string }>;
  integrations: { voice: string; avatar: string; whatsapp: string; email: string; aiScoring: boolean; videoUploads: boolean };
}

export interface LeadFields {
  name: string;
  phone: string;
  email?: string;
  city?: string;
  genres?: string[];
  genreText?: string;
  experienceYears?: number;
  languages?: string[];
  qualification?: string;
  portfolioUrl?: string;
  sourceDetail?: string;
  notes?: string;
}

export interface Stats {
  days: number;
  leads: number;
  leadsChangePct: number | null;
  awaitingReview: number;
  funnel: Array<{ stage: Stage; label: string; count: number; pctOfLeads: number; pctOfPrevious: number }>;
}

export interface Filters {
  q?: string;
  genre?: string;
  source?: string;
  owner?: string;
}

const query = (params: Record<string, string | number | undefined>) => {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}` : '';
};

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

export const getMeta = () => hiringApi<HiringMeta>('/v1/meta');
export const getBoard = (f: Filters) => hiringApi<{ columns: BoardColumn[] }>(`/v1/candidates/board${query({ ...f })}`);
export const listCandidates = (f: Filters & { stage?: string; stageStatus?: string; sort?: string; page?: number; pageSize?: number }) =>
  hiringApi<{ total: number; page: number; pageSize: number; candidates: Card[] }>(`/v1/candidates${query({ ...f })}`);
export const getCandidate = (id: string) => hiringApi<CandidateDetail>(`/v1/candidates/${id}`);
export const createCandidate = (body: LeadFields & { source: LeadSource; ownerEmail?: string; autoScreen?: boolean }) =>
  hiringApi<{ id: string; created: boolean }>('/v1/candidates', { method: 'POST', body });
export const importCandidates = (body: { source: LeadSource; sourceDetail?: string; autoScreen?: boolean; rows: LeadFields[] }) =>
  hiringApi<{ created: number; duplicates: number; failed: Array<{ row: number; phone: string; error: string }> }>(
    '/v1/candidates/import',
    { method: 'POST', body },
  );
export const updateCandidate = (id: string, body: Record<string, unknown>) =>
  hiringApi<CandidateDetail>(`/v1/candidates/${id}`, { method: 'PATCH', body });
export const moveCandidate = (
  id: string,
  body: { toStage: Stage; reason?: string; notify?: boolean; trainingBatch?: string; trainingStartAt?: string; contractUrl?: string; cmsTeacherId?: string },
) => hiringApi(`/v1/candidates/${id}/move`, { method: 'POST', body });
export const getPortalLink = (id: string) =>
  hiringApi<{ url: string; expiresAt: string }>(`/v1/candidates/${id}/portal-link`, { method: 'POST' });
export const addNote = (id: string, note: string) => hiringApi(`/v1/candidates/${id}/notes`, { method: 'POST', body: { note } });
export const callNow = (id: string, immediate: boolean) =>
  hiringApi(`/v1/candidates/${id}/screening-calls`, { method: 'POST', body: { immediate } });
export const rescoreCall = (callId: string) => hiringApi(`/v1/screening-calls/${callId}/rescore`, { method: 'POST' });
export const resendInvite = (id: string) => hiringApi(`/v1/candidates/${id}/interviews/ai/invite`, { method: 'POST' });
export const scheduleAcademic = (id: string, body: { scheduledAt: string; interviewerEmail: string; meetingUrl: string }) =>
  hiringApi(`/v1/candidates/${id}/interviews/academic`, { method: 'POST', body });
export const decideInterview = (interviewId: string, body: { result?: string; status?: string; feedback?: string; scorecard?: AcademicScorecard }) =>
  hiringApi(`/v1/interviews/${interviewId}`, { method: 'PATCH', body });
export const rescoreInterview = (interviewId: string) => hiringApi(`/v1/interviews/${interviewId}/rescore`, { method: 'POST' });
export const getInterviewRecording = (interviewId: string) => hiringApi<{ url: string }>(`/v1/interviews/${interviewId}/recording`);
export const getInterviewVideo = (interviewId: string) => hiringApi<{ url: string }>(`/v1/interviews/${interviewId}/video`);
export const getStats = (days: number) => hiringApi<Stats>(`/v1/candidates/stats?days=${days}`);
export const getGenres = () => hiringApi<Genre[]>('/v1/genres');
export const createGenre = (body: { id: string; name: string }) => hiringApi<Genre>('/v1/genres', { method: 'POST', body });
export const updateGenre = (id: string, body: Partial<Pick<Genre, 'name' | 'active' | 'questions' | 'interviewLanguages'>> & { brochureUrl?: string }) =>
  hiringApi<Genre>(`/v1/genres/${id}`, { method: 'PATCH', body });
export const getSettings = () => hiringApi<HiringSettings>('/v1/settings');
export const saveSettings = (body: HiringSettings) => hiringApi<HiringSettings>('/v1/settings', { method: 'PUT', body });

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

/** "3d", "5h", "12m": how long a card has sat in its stage. */
export function waited(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h`;
  return `${Math.round(minutes / 1440)}d`;
}

export const when = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })
    : '—';

/** Splits a CSV file (quoted fields, commas, CRLF) into rows of cells. */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      if (row.some((c) => c.trim())) rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  row.push(cell);
  if (row.some((c) => c.trim())) rows.push(row);
  return rows;
}

/** "2 hrs ago", "1 day ago", "Just now". */
export function ago(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 2) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/** The candidate's latest score, from whichever round scored them last. */
export function latestScore(card: Pick<Card, 'screening' | 'interview'>): number | null {
  if (card.interview?.score != null) return card.interview.score;
  return card.screening?.score ?? null;
}

export type DrawerTab = 'overview' | 'call' | 'interview' | 'academic' | 'timeline';

/**
 * What an agent should do next for a candidate, so the table can put one
 * button on each row. `primary` = waiting on us; `move` opens the move dialog.
 */
export interface NextAction {
  label: string;
  hint: string;
  primary: boolean;
  tab?: DrawerTab;
  move?: Stage;
  /** The drawer's second button, where there is an obvious one. */
  secondary?: { label: string; tab?: DrawerTab; move?: Stage };
}

export function nextAction(c: Pick<Card, 'stage' | 'stageStatus'>): NextAction {
  const review = c.stageStatus === 'AWAITING_REVIEW';
  switch (c.stage) {
    case 'NEW':
      return { label: 'Start AI call', hint: 'Not screened yet. Start the AI screening call.', primary: false, move: 'SCREENING' };
    case 'SCREENING':
      return review
        ? {
            label: 'Review call',
            hint: 'The AI call is done. Review the score and transcript, then send the interview link.',
            primary: true,
            tab: 'call',
            secondary: { label: 'Send interview link', move: 'INTERVIEW_1' },
          }
        : { label: 'View call', hint: 'The AI screening call is scheduled or in progress.', primary: false, tab: 'call' };
    case 'INTERVIEW_1':
      return review
        ? {
            label: 'Watch recording',
            hint: 'Video interview and performance video submitted. Watch both and decide.',
            primary: true,
            tab: 'interview',
            secondary: { label: 'Move to academic', move: 'INTERVIEW_2' },
          }
        : { label: 'Track interview', hint: 'Waiting for the candidate to take the AI interview.', primary: false, tab: 'interview' };
    case 'INTERVIEW_2':
      if (review)
        return {
          label: 'Review scorecard',
          hint: 'The academic interviewer has scored them. Decide on training.',
          primary: true,
          tab: 'academic',
          secondary: { label: 'Move to training', move: 'TRAINING' },
        };
      return c.stageStatus === 'SCHEDULED'
        ? { label: 'Add scorecard', hint: 'Academic interview scheduled. Add the scorecard after it.', primary: false, tab: 'academic' }
        : { label: 'Schedule interview', hint: 'Schedule the academic interview with a faculty member.', primary: true, tab: 'academic' };
    case 'TRAINING':
      return { label: 'Send agreement', hint: 'In training. Send the agreement when they complete it.', primary: false, move: 'CONTRACT' };
    case 'CONTRACT':
      return { label: 'Mark onboarded', hint: 'Agreement sent. Mark them onboarded once it is signed.', primary: false, move: 'ONBOARDED' };
    default:
      return { label: 'View', hint: 'No action needed.', primary: false, tab: 'overview' };
  }
}
